import { Modal, Form, Input, Select } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FlatSection, SectionFormValues } from "./types";
import { getThemeColor } from "./taskHubHelpers";
import { SectionOptionLabel } from "./SectionOptionLabel";

interface SectionModalProps {
  open: boolean;
  mode: "create" | "edit";
  /** Sections selectable as parent (already excludes invalid targets). */
  sectionOptions: FlatSection[];
  initialValues?: Partial<SectionFormValues>;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (values: SectionFormValues) => void;
}

export const SectionModal: React.FC<SectionModalProps> = ({
  open,
  mode,
  sectionOptions,
  initialValues,
  loading,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<SectionFormValues>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        name: initialValues?.name ?? "",
        parent_section: initialValues?.parent_section ?? null,
      });
    } else {
      form.resetFields();
    }
  }, [open, initialValues?.name, initialValues?.parent_section, form]);

  const themeColor = getThemeColor();

  const rootLabel = t("taskHub.sections.rootLevel") || "Top Level";

  // `name` is the plain searchable string (and what shows when selected);
  // `label` is the indented JSX shown in the dropdown list.
  const options = [
    {
      value: null,
      name: rootLabel,
      label: (
        <span className="flex items-center text-gray-500">
          <HomeOutlined className="mr-1.5" />
          {rootLabel}
        </span>
      ),
    },
    ...sectionOptions.map((s) => ({
      value: s.id,
      name: s.name,
      label: <SectionOptionLabel name={s.name} depth={s.depth} />,
    })),
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={loading}
      destroyOnClose
      title={
        mode === "create"
          ? t("taskHub.sections.addSection") || "Add Section"
          : t("taskHub.sections.editSection") || "Edit Section"
      }
      okButtonProps={{
        style: { backgroundColor: themeColor, color: "#fff" },
      }}
      okText={t("taskHub.modal.ok") || "OK"}
      cancelText={t("taskHub.modal.cancel") || "Cancel"}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) =>
          onSubmit({
            name: values.name,
            parent_section: values.parent_section ?? null,
          })
        }
      >
        <Form.Item
          name="name"
          label={t("taskHub.modal.name") || "Name"}
          rules={[{ required: true }]}
        >
          <Input
            placeholder={
              t("taskHub.sections.enterSectionName") || "Enter section name"
            }
          />
        </Form.Item>

        <Form.Item
          name="parent_section"
          label={t("taskHub.sections.parentSection") || "Parent section"}
        >
          <Select
            allowClear
            showSearch
            optionLabelProp="name"
            filterOption={(input, option) =>
              (option?.name ?? "").toLowerCase().includes(input.toLowerCase())
            }
            placeholder={rootLabel}
            options={options}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
